import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { Trash2 } from "lucide-react";

const eur = v => v == null ? "—" : `€${Math.round(v).toLocaleString("it-IT")}`;
const ALERT_UI = {
  OK: ["OK", "#10B981"], ATTENZIONE: ["Attenzione", "#D97706"],
  TENSIONE: ["Tensione", "#FF6A24"], CRITICO: ["Critico", "#DC2626"],
};
const CATS_IN = [["incassi_clienti", "Incassi clienti"], ["altri_incassi", "Altri incassi"]];
const CATS_OUT = [["pagamenti_fornitori", "Pagamenti fornitori"], ["personale", "Personale"],
  ["imposte", "Imposte"], ["debito_rate", "Debito/rate"], ["affitto", "Affitto"],
  ["costi_ricorrenti", "Costi ricorrenti"], ["capex", "CAPEX"], ["altri_pagamenti", "Altri pagamenti"]];

export default function CashFlow() {
  const [settings, setSettings] = useState(null);
  const [movements, setMovements] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [err, setErr] = useState(null);
  const [cashInput, setCashInput] = useState("");
  const [mv, setMv] = useState({ direction: "IN", category: "incassi_clienti", amount: "", date: "", recurrence: "NONE", note: "" });

  const load = () => {
    api.get("/cashflow/settings").then(r => setSettings(r.data)).catch(e => {
      if (String(formatApiError(e)).includes("upgrade_required")) setErr("locked");
    });
    api.get("/cashflow/movements").then(r => setMovements(r.data.movements || [])).catch(() => {});
    api.get("/cashflow/forecast").then(r => setForecast(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const saveCash = async () => {
    try {
      await api.put("/cashflow/settings", { opening_cash: Number(cashInput) });
      toast.success("Saldo iniziale salvato");
      setCashInput("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const addMovement = async () => {
    try {
      await api.post("/cashflow/movements", { ...mv, amount: Number(mv.amount) });
      toast.success("Movimento aggiunto");
      setMv({ direction: "IN", category: "incassi_clienti", amount: "", date: "", recurrence: "NONE", note: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delMovement = async (id) => {
    try { await api.delete(`/cashflow/movements/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  if (err === "locked") return (
    <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="cf-locked">
      <h1 className="text-3xl font-extrabold">Cash Flow Forecast</h1>
      <p className="text-[#524F5E] mt-3">Il previsionale di cassa 30/60/90 giorni è disponibile dal piano <b>CONTROL</b>.</p>
      <Link to="/pricing" className="ic-btn-primary inline-block mt-6" data-testid="cf-pricing">Vedi i piani</Link>
    </div>
  );
  if (!settings || !forecast) return <div className="max-w-6xl mx-auto px-4 py-20 text-center">Caricamento…</div>;

  const alert = ALERT_UI[forecast.alert] || ALERT_UI.OK;
  const cats = mv.direction === "IN" ? CATS_IN : CATS_OUT;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="cf-page">
      <p className="text-xs uppercase tracking-widest text-[#FF6A24] font-bold">Cash Flow Forecast</p>
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Previsione di cassa 30/60/90 giorni</h1>
      <p className="text-sm text-[#7A7788] mt-2">Solo movimenti che inserisci tu: nessuna scadenza inventata. {forecast.cash_not_profit} <Link to="/cfo-simulator" className="text-[#5424D5] font-semibold" data-testid="cf-cfo-link">Apri il CFO Simulator</Link></p>

      {forecast.status === "MISSING_OPENING_CASH" && (
        <div className="ic-card p-6 mt-8 border-[#FF6A24]/40" data-testid="cf-missing-cash">
          <h2 className="font-bold text-lg">Per iniziare: qual è il tuo saldo attuale?</h2>
          <p className="text-sm text-[#524F5E] mt-1">Inserisci il saldo di banca/cassa ad oggi. Poi aggiungi incassi e pagamenti attesi con le date.</p>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        <div className="space-y-6">
          <div className="ic-card p-6" data-testid="cf-settings">
            <h2 className="font-bold mb-3">Saldo iniziale</h2>
            <div className="flex gap-3">
              <input type="number" className="ic-input flex-1" placeholder="es. 12500" value={cashInput}
                onChange={e => setCashInput(e.target.value)} data-testid="cf-opening-cash" />
              <button className="ic-btn-primary" onClick={saveCash} disabled={cashInput === ""} data-testid="cf-save-cash">Salva</button>
            </div>
            {settings.opening_cash != null && <p className="text-xs text-[#7A7788] mt-2">Saldo attuale: <b>{eur(settings.opening_cash)}</b> (al {settings.as_of})</p>}
          </div>

          <div className="ic-card p-6" data-testid="cf-add-movement">
            <h2 className="font-bold mb-3">Aggiungi movimento previsto</h2>
            <div className="grid grid-cols-2 gap-3">
              <select className="ic-input" value={mv.direction} data-testid="cf-mv-direction"
                onChange={e => setMv({ ...mv, direction: e.target.value, category: e.target.value === "IN" ? "incassi_clienti" : "pagamenti_fornitori" })}>
                <option value="IN">Incasso</option><option value="OUT">Pagamento</option>
              </select>
              <select className="ic-input" value={mv.category} data-testid="cf-mv-category"
                onChange={e => setMv({ ...mv, category: e.target.value })}>
                {cats.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <input type="number" className="ic-input" placeholder="Importo €" value={mv.amount}
                onChange={e => setMv({ ...mv, amount: e.target.value })} data-testid="cf-mv-amount" />
              <input type="date" className="ic-input" value={mv.date}
                onChange={e => setMv({ ...mv, date: e.target.value })} data-testid="cf-mv-date" />
              <select className="ic-input" value={mv.recurrence} data-testid="cf-mv-recurrence"
                onChange={e => setMv({ ...mv, recurrence: e.target.value })}>
                <option value="NONE">Una tantum</option><option value="MONTHLY">Mensile (ogni 30 gg)</option>
              </select>
              <input className="ic-input" placeholder="Nota (facoltativa)" value={mv.note}
                onChange={e => setMv({ ...mv, note: e.target.value })} data-testid="cf-mv-note" />
            </div>
            <button className="ic-btn-primary mt-4" onClick={addMovement}
              disabled={!mv.amount || !mv.date} data-testid="cf-mv-add">Aggiungi</button>
          </div>

          {movements.length > 0 && (
            <div className="ic-card p-6" data-testid="cf-movements">
              <h2 className="font-bold mb-3">Movimenti inseriti ({movements.length})</h2>
              <div className="space-y-2">
                {movements.map(m => (
                  <div key={m.movement_id} className="flex items-center justify-between text-sm border-b border-[#F4F4F7] pb-2 last:border-0" data-testid={`cf-mv-${m.movement_id}`}>
                    <span className={m.direction === "IN" ? "text-[#10B981] font-semibold" : "text-[#DC2626] font-semibold"}>
                      {m.direction === "IN" ? "+" : "−"}{eur(m.amount)}
                    </span>
                    <span className="text-[#524F5E]">{(forecast.category_labels || {})[m.category] || m.category}{m.recurrence === "MONTHLY" ? " · mensile" : ""}</span>
                    <span className="text-[#7A7788] text-xs">{m.date}</span>
                    <button onClick={() => delMovement(m.movement_id)} className="text-[#7A7788] hover:text-[#DC2626]" data-testid={`cf-mv-del-${m.movement_id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          {forecast.status === "OK" && (
            <>
              <div className="ic-card p-6 mb-6" data-testid="cf-forecast-cards">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-bold">Previsione</h2>
                  <span className="ic-badge" style={{ color: alert[1] }} data-testid="cf-alert">{alert[0]}</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[["Oggi", forecast.opening_cash], ["Tra 30 giorni", forecast.cash_30], ["Tra 60 giorni", forecast.cash_60], ["Tra 90 giorni", forecast.cash_90]].map(([l, v], i) => (
                    <div key={i} className="bg-[#F4F4F7] rounded-xl p-3" data-testid={`cf-card-${i}`}>
                      <p className="text-[10px] uppercase tracking-widest text-[#7A7788]">{l}</p>
                      <p className={`font-extrabold ${v < 0 ? "text-[#DC2626]" : ""}`}>{eur(v)}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 text-sm space-y-1">
                  <p>Minimo previsto: <b className={forecast.min_cash < 0 ? "text-[#DC2626]" : ""}>{eur(forecast.min_cash)}</b> il {forecast.min_date?.split("-").reverse().join("/")}</p>
                  <p>Variazione a 90 giorni: <b className={forecast.delta_90 < 0 ? "text-[#DC2626]" : "text-[#10B981]"}>{forecast.delta_90 > 0 ? "+" : ""}{eur(forecast.delta_90)}</b></p>
                  <p className="text-xs text-[#7A7788]">Incassi previsti {eur(forecast.total_inflows)} · Pagamenti previsti {eur(forecast.total_outflows)} · Alert policy v{forecast.alert_policy_version}</p>
                </div>
              </div>
              <div className="ic-card p-6" data-testid="cf-chart">
                <h2 className="font-bold mb-3">Timeline giornaliera</h2>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={forecast.timeline}>
                    <XAxis dataKey="date" tickFormatter={d => d.slice(5).split("-").reverse().join("/")} fontSize={11} interval={13} />
                    <YAxis fontSize={11} tickFormatter={v => `€${(v / 1000).toLocaleString("it-IT")}k`} />
                    <Tooltip formatter={v => eur(v)} labelFormatter={d => String(d).split("-").reverse().join("/")} />
                    <ReferenceLine y={0} stroke="#DC2626" strokeDasharray="4 4" />
                    <Line type="stepAfter" dataKey="cash" stroke="#5424D5" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
